import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { getAdminSocket } from '@/services/socket';

function eventPayload(event) {
  if (event?.payload && typeof event.payload === 'object') return event.payload;
  return event && typeof event === 'object' ? event : {};
}

function idValue(value) {
  if (value == null) return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
}

export function locationKey(item) {
  return `${idValue(item?.tripId || item?.trip?.id)}:${idValue(item?.memberId || item?.member?.id)}`;
}

export function locationTime(item) {
  const value = item?.recordedAt || item?.receivedAt || item?.updatedAt || item?.timestamp || 0;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

export function mergeLiveLocations(current, incoming) {
  const values = new Map((current || []).map((item) => [locationKey(item), item]));
  for (const item of incoming || []) {
    const key = locationKey(item);
    if (!key || key === ':') continue;
    const previous = values.get(key);
    if (!previous || locationTime(item) >= locationTime(previous)) {
      values.set(key, { ...previous, ...item });
    }
  }
  return [...values.values()].filter((item) => item?.trip?.status !== 'completed' && item?.trip?.status !== 'cancelled');
}

export function useLiveLocations({ refetchInterval = 10_000 } = {}) {
  const { data, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['admin-live-locations'],
    queryFn: () => api('/api/v1/admin/live-locations').then((result) => result.data),
    refetchInterval,
    refetchIntervalInBackground: true,
  });
  const [locations, setLocations] = useState([]);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const cursorRef = useRef(null);
  const recoveryRef = useRef(false);

  const rememberCursor = useCallback((event) => {
    const timestamp = event?.timestamp || event?.occurredAt;
    if (!timestamp) return;
    if (!cursorRef.current || new Date(timestamp) > new Date(cursorRef.current)) {
      cursorRef.current = timestamp;
    }
  }, []);

  const applyLocation = useCallback((event) => {
    rememberCursor(event);
    const payload = eventPayload(event);
    const tripId = idValue(payload.tripId || payload.trip?.id || event?.tripId);
    const memberId = idValue(payload.memberId || payload.member?.id || event?.memberId);
    if (!tripId || !memberId || payload.trip?.status === 'completed' || payload.trip?.status === 'cancelled') return;
    setLocations((current) => mergeLiveLocations(current, [{ ...payload, tripId, memberId }]));
  }, [rememberCursor]);

  const applyDestination = useCallback((event) => {
    rememberCursor(event);
    const payload = eventPayload(event);
    const trip = payload.trip;
    const tripId = idValue(event?.tripId || trip?._id || trip?.id || payload.tripId);
    if (!tripId) return;
    setLocations((current) => current.map((item) => idValue(item.tripId || item.trip?.id) === tripId
      ? {
          ...item,
          trip: trip || {
            ...item.trip,
            destination: payload.destination,
            route: payload.route,
          },
        }
      : item));
  }, [rememberCursor]);

  const applyStatus = useCallback((event) => {
    rememberCursor(event);
    const payload = eventPayload(event);
    const trip = payload.trip;
    const tripId = idValue(event?.tripId || trip?._id || trip?.id || payload.tripId);
    const status = trip?.status || payload.status;
    if (!tripId) return;
    if (status && status !== 'active') {
      setLocations((current) => current.filter((item) => idValue(item.tripId || item.trip?.id) !== tripId));
    }
  }, [rememberCursor]);

  const applySafety = useCallback((event) => {
    rememberCursor(event);
    const payload = eventPayload(event);
    const tripId = idValue(event?.tripId || payload.tripId);
    const memberId = idValue(event?.memberId || payload.memberId);
    if (!tripId || !memberId) return;
    setLocations((current) => current.map((item) => {
      if (locationKey(item) !== `${tripId}:${memberId}`) return item;
      const stage = payload.stage || item.connectivityStage;
      return {
        ...item,
        connectivityStage: stage,
        rideStatus: stage === 'emergency_active'
          ? 'emergency'
          : stage === 'recovered'
            ? (Number(item.speedKmh || 0) >= 2 ? 'riding' : 'stopped')
            : item.rideStatus,
        deviceHealth: payload.deviceHealth || item.deviceHealth,
        lastReliablePosition: payload.lastReliablePosition || item.lastReliablePosition,
      };
    }));
  }, [rememberCursor]);

  const applyPersistedEvent = useCallback((event) => {
    switch (event?.event) {
      case 'location:updated':
        applyLocation(event);
        break;
      case 'trip:destination_updated':
        applyDestination(event);
        break;
      case 'trip:status_updated':
        applyStatus(event);
        break;
      case 'ride:device_health':
      case 'ride:connectivity_emergency':
      case 'ride:safe_check':
        applySafety(event);
        break;
      default:
        break;
    }
  }, [applyDestination, applyLocation, applySafety, applyStatus]);

  const recoverRealtime = useCallback(async (socket) => {
    if (recoveryRef.current) return;
    recoveryRef.current = true;
    try {
      const snapshot = await refetch();
      if (Array.isArray(snapshot.data)) {
        setLocations((current) => mergeLiveLocations(snapshot.data, current));
        const newest = [...snapshot.data].sort((a, b) => locationTime(b) - locationTime(a))[0];
        const newestTime = newest?.recordedAt || newest?.receivedAt || newest?.updatedAt;
        if (newestTime && !cursorRef.current) cursorRef.current = newestTime;
      }
      if (!socket.connected) return;
      socket.emit('admin:state_resync', {
        after: cursorRef.current,
        limit: 1000,
      }, (response) => {
        if (response?.ok !== true) return;
        for (const event of response?.data?.events || []) applyPersistedEvent(event);
        if (response?.data?.cursor) cursorRef.current = response.data.cursor;
      });
    } finally {
      recoveryRef.current = false;
    }
  }, [applyPersistedEvent, refetch]);

  useEffect(() => {
    if (Array.isArray(data)) {
      setLocations(data);
    }
  }, [data]);

  useEffect(() => {
    if (!locations.length) return undefined;
    const capture = () => setSnapshots((current) => {
      const latest = current[current.length - 1];
      const signature = locations.map((item) => `${locationKey(item)}:${locationTime(item)}`).sort().join('|');
      if (latest?.signature === signature) return current;
      return [...current, {
        capturedAt: new Date().toISOString(),
        signature,
        locations: locations.map((item) => ({ ...item })),
      }].slice(-180);
    });
    capture();
    const timer = window.setInterval(capture, 5_000);
    return () => window.clearInterval(timer);
  }, [locations]);

  useEffect(() => {
    let socket;
    try {
      socket = getAdminSocket();
    } catch {
      setRealtimeConnected(false);
      return undefined;
    }
    const connectHandler = () => {
      setRealtimeConnected(true);
      void recoverRealtime(socket);
    };
    const disconnectHandler = () => setRealtimeConnected(false);
    const readyHandler = () => void recoverRealtime(socket);
    const statusHandler = (event) => {
      applyStatus(event);
      const payload = eventPayload(event);
      if ((payload.trip?.status || payload.status) === 'active') {
        void recoverRealtime(socket);
      }
    };
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') void recoverRealtime(socket);
    };
    const onlineHandler = () => void recoverRealtime(socket);

    setRealtimeConnected(socket.connected);
    socket.on('connect', connectHandler);
    socket.on('connection:ready', readyHandler);
    socket.on('disconnect', disconnectHandler);
    socket.on('location:updated', applyLocation);
    socket.on('trip:destination_updated', applyDestination);
    socket.on('trip:status_updated', statusHandler);
    socket.on('ride:device_health', applySafety);
    socket.on('ride:connectivity_emergency', applySafety);
    socket.on('ride:safe_check', applySafety);
    document.addEventListener('visibilitychange', visibilityHandler);
    window.addEventListener('online', onlineHandler);
    if (socket.connected) void recoverRealtime(socket);

    return () => {
      socket.off('connect', connectHandler);
      socket.off('connection:ready', readyHandler);
      socket.off('disconnect', disconnectHandler);
      socket.off('location:updated', applyLocation);
      socket.off('trip:destination_updated', applyDestination);
      socket.off('trip:status_updated', statusHandler);
      socket.off('ride:device_health', applySafety);
      socket.off('ride:connectivity_emergency', applySafety);
      socket.off('ride:safe_check', applySafety);
      document.removeEventListener('visibilitychange', visibilityHandler);
      window.removeEventListener('online', onlineHandler);
    };
  }, [applyDestination, applyLocation, applySafety, applyStatus, recoverRealtime]);

  return {
    locations,
    snapshots,
    realtimeConnected,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}
